import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Download, FileDown, Inbox, Loader2, Pencil, Plus, Search, Share2, Trash2, X } from 'lucide-react';
import Layout from '../components/Layout.js';
import Modal from '../components/Modal.js';
import LeadShareDialog from '../components/LeadShareDialog.js';
import { useTier } from '../hooks/useTier.js';
import { UpgradePrompt } from '../components/UpgradePrompt.js';
import { useAppTranslation } from '../hooks/useAppTranslation.js';
import { readError } from './LinkPages.js';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface Lead {
  id: string;
  userId: string;
  name: string;
  company: string | null;
  jobTitle: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  street: string | null;
  zip: string | null;
  city: string | null;
  country: string | null;
  note: string | null;
  source: 'manual' | 'scan' | 'card_tap' | 'import';
  status: 'new' | 'contacted' | 'qualified' | 'converted' | 'archived';
  createdAt: string;
  ownerEmail?: string;
  /** owner/admin: full control; edit: shared as editable; view: shared read-only */
  access?: 'owner' | 'admin' | 'edit' | 'view';
}

interface LeadFormState {
  name: string;
  company: string;
  jobTitle: string;
  email: string;
  phone: string;
  website: string;
  street: string;
  zip: string;
  city: string;
  country: string;
  note: string;
  source: Lead['source'];
  status: Lead['status'];
}

const EMPTY_FORM: LeadFormState = {
  name: '', company: '', jobTitle: '', email: '', phone: '', website: '',
  street: '', zip: '', city: '', country: '', note: '',
  source: 'manual', status: 'new',
};

const STATUS_KEYS: Lead['status'][] = ['new', 'contacted', 'qualified', 'converted', 'archived'];

/** A queued background scan in the capture inbox. */
interface ScanItem {
  id: string;
  status: 'pending' | 'processing' | 'done' | 'failed';
  error: string | null;
  createdAt: string;
}

/** Fields the user can assign an unmapped OCR line to (labels reuse field keys). */
const ASSIGNABLE_FIELDS: Array<{ key: keyof LeadFormState; labelKey: string; fallback: string }> = [
  { key: 'name', labelKey: 'leads.fieldName', fallback: 'Name' },
  { key: 'company', labelKey: 'leads.fieldCompany', fallback: 'Firma' },
  { key: 'jobTitle', labelKey: 'leads.fieldJobTitle', fallback: 'Position' },
  { key: 'email', labelKey: 'leads.fieldEmail', fallback: 'E-Mail' },
  { key: 'phone', labelKey: 'leads.fieldPhone', fallback: 'Telefon' },
  { key: 'website', labelKey: 'leads.fieldWebsite', fallback: 'Website' },
  { key: 'street', labelKey: 'leads.fieldStreet', fallback: 'Straße & Nr.' },
  { key: 'zip', labelKey: 'leads.fieldZip', fallback: 'PLZ' },
  { key: 'city', labelKey: 'leads.fieldCity', fallback: 'Stadt' },
  { key: 'country', labelKey: 'leads.fieldCountry', fallback: 'Land' },
  { key: 'note', labelKey: 'leads.fieldNote', fallback: 'Notiz' },
];

const STATUS_STYLES: Record<Lead['status'], string> = {
  new: 'bg-accent-500/10 text-accent-600 dark:text-accent-300 border-accent-500/30',
  contacted: 'bg-warning-500/10 text-warning-600 dark:text-warning-300 border-warning-500/30',
  qualified: 'bg-success-500/10 text-success-600 dark:text-success-300 border-success-500/30',
  converted: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-300 border-emerald-500/30',
  archived: 'bg-raised text-ink-faint border-line',
};

function toForm(lead: Lead): LeadFormState {
  return {
    name: lead.name ?? '',
    company: lead.company ?? '',
    jobTitle: lead.jobTitle ?? '',
    email: lead.email ?? '',
    phone: lead.phone ?? '',
    website: lead.website ?? '',
    street: lead.street ?? '',
    zip: lead.zip ?? '',
    city: lead.city ?? '',
    country: lead.country ?? '',
    note: lead.note ?? '',
    source: lead.source,
    status: lead.status,
  };
}

// ─── Component ───────────────────────────────────────────────────────────────

function LeadsPage() {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [leads, setLeads] = useState<Lead[] | null>(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [editing, setEditing] = useState<Lead | 'new' | null>(null);
  const [form, setForm] = useState<LeadFormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [deleting, setDeleting] = useState<Lead | null>(null);
  const [sharing, setSharing] = useState<Lead | null>(null);
  // OCR lines that could not be mapped automatically — user assigns them manually.
  const [unassigned, setUnassigned] = useState<string[]>([]);
  // Learning feedback context (only for scanned leads): manual assignments +
  // anonymous layout stats. Sent after save, then discarded.
  const [manualAssignments, setManualAssignments] = useState<Array<{ field: 'jobTitle' | 'company'; line: string }>>([]);
  const [scanMeta, setScanMeta] = useState<{ nameFontHeightRatio?: number; nameIndexPct?: number } | null>(null);
  // Batch capture inbox: queued/finished background scans.
  const [scans, setScans] = useState<ScanItem[]>([]);
  // Set while the editor is prefilled from an inbox scan — saving consumes it.
  const [activeScanId, setActiveScanId] = useState<string | null>(null);

  const loadScans = useCallback(async () => {
    const res = await fetch('/api/leads/scans', { credentials: 'include' });
    if (res.ok) setScans((await res.json()) as ScanItem[]);
  }, []);

  // Poll the inbox while any scan is still being processed.
  const hasOpenScans = scans.some((s) => s.status === 'pending' || s.status === 'processing');
  useEffect(() => {
    void loadScans();
    if (!hasOpenScans) return;
    const timer = setInterval(() => void loadScans(), 5000);
    return () => clearInterval(timer);
  }, [loadScans, hasOpenScans]);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (search.trim()) params.set('q', search.trim());
    if (statusFilter) params.set('status', statusFilter);
    const res = await fetch(`/api/leads?${params.toString()}`, { credentials: 'include' });
    if (res.status === 401) { navigate('/login'); return; }
    if (!res.ok) { setError(await readError(res)); return; }
    setLeads((await res.json()) as Lead[]);
  }, [navigate, search, statusFilter]);

  useEffect(() => {
    const handle = setTimeout(() => void load(), search ? 250 : 0);
    return () => clearTimeout(handle);
  }, [load, search]);

  // ─── Editor ──────────────────────────────────────────────────────────────

  function openNew(prefill?: Partial<LeadFormState>, unassignedLines: string[] = []) {
    setForm({ ...EMPTY_FORM, ...prefill });
    setUnassigned(unassignedLines);
    setManualAssignments([]);
    setScanMeta(null);
    setActiveScanId(null);
    setEditing('new');
    setError('');
  }

  function openEdit(lead: Lead) {
    setForm(toForm(lead));
    setUnassigned([]);
    setManualAssignments([]);
    setScanMeta(null);
    setActiveScanId(null);
    setEditing(lead);
    setError('');
  }

  /** Assign an unmapped OCR line to a form field chosen by the user. */
  function assignLine(line: string, key: keyof LeadFormState) {
    setForm((prev) => ({
      ...prev,
      [key]: key === 'note'
        ? (prev.note ? `${prev.note}\n${line}` : line)
        : line,
    }));
    // Only generic business vocabulary fields feed the learning loop.
    if (key === 'jobTitle' || key === 'company') {
      setManualAssignments((prev) => [...prev, { field: key, line }]);
    }
    setUnassigned((prev) => prev.filter((l) => l !== line));
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) { setError(t('leads.errorNameRequired', 'Bitte einen Namen angeben.')); return; }
    setSaving(true);
    setError('');
    const isNew = editing === 'new';
    const body = JSON.stringify({
      name: form.name.trim(),
      company: form.company.trim() || null,
      jobTitle: form.jobTitle.trim() || null,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      website: form.website.trim() || null,
      street: form.street.trim() || null,
      zip: form.zip.trim() || null,
      city: form.city.trim() || null,
      country: form.country.trim() || null,
      note: form.note.trim() || null,
      source: form.source,
      status: form.status,
      // A lead saved from an inbox scan consumes that scan server-side.
      ...(isNew && activeScanId ? { scanId: activeScanId } : {}),
    });
    const res = await fetch(isNew ? '/api/leads' : `/api/leads/${(editing as Lead).id}`, {
      method: isNew ? 'POST' : 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body,
    });
    setSaving(false);
    if (!res.ok) { setError(await readError(res)); return; }

    // Learning feedback: fire-and-forget, only for scan-sourced leads.
    // Sends manual assignments (generic vocabulary only, server-side PII
    // filter) plus anonymous layout stats — never full card content.
    if (form.source === 'scan' && (manualAssignments.length > 0 || scanMeta)) {
      void fetch('/api/leads/scan/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          assignments: manualAssignments,
          excludeValues: [form.name, form.email, form.phone].filter(Boolean),
          ...(scanMeta ? { layout: scanMeta } : {}),
        }),
      }).catch(() => {});
    }

    setEditing(null);
    setManualAssignments([]);
    setScanMeta(null);
    setActiveScanId(null);
    void load();
    void loadScans();
  }

  // ─── Business card scan ──────────────────────────────────────────────────

  async function scan(file: File) {
    setScanning(true);
    setError('');
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch('/api/leads/scan', { method: 'POST', credentials: 'include', body: fd });
    setScanning(false);
    if (!res.ok) {
      setError(await readError(res));
      openNew({ source: 'scan' }); // fall back to manual entry
      return;
    }
    const data = (await res.json()) as {
      suggestion: Partial<LeadFormState> & {
        unassigned?: string[];
        meta?: { medianFontHeight?: number; nameFontHeight?: number; nameIndexPct?: number };
      };
    };
    const s = data.suggestion;
    openNew({
      name: s.name ?? '',
      company: s.company ?? '',
      jobTitle: s.jobTitle ?? '',
      email: s.email ?? '',
      phone: s.phone ?? '',
      website: s.website ?? '',
      street: s.street ?? '',
      zip: s.zip ?? '',
      city: s.city ?? '',
      country: s.country ?? '',
      source: 'scan',
    }, s.unassigned ?? []);

    // Anonymous layout stats for the learning loop (numbers only, no content).
    const meta = s.meta;
    if (meta?.nameFontHeight !== undefined && meta.medianFontHeight) {
      setScanMeta({
        nameFontHeightRatio: meta.nameFontHeight / meta.medianFontHeight,
        ...(meta.nameIndexPct !== undefined ? { nameIndexPct: meta.nameIndexPct } : {}),
      });
    }
  }

  // ─── Batch capture (scan inbox) ────────────────────────────────────────────

  /** Queue several card photos for background processing. */
  async function queueBatch(files: File[]) {
    setScanning(true);
    setError('');
    const fd = new FormData();
    for (const f of files) fd.append('files', f);
    const res = await fetch('/api/leads/scans', { method: 'POST', credentials: 'include', body: fd });
    setScanning(false);
    if (!res.ok) { setError(await readError(res)); return; }
    void loadScans();
  }

  /** Route file selection: one card scans immediately, several go to the queue. */
  function handleFiles(list: FileList | null) {
    const files = Array.from(list ?? []);
    if (files.length === 1 && files[0]) void scan(files[0]);
    else if (files.length > 1) void queueBatch(files);
  }

  /** Open a finished scan's suggestion in the editor. */
  async function reviewScan(item: ScanItem) {
    const res = await fetch(`/api/leads/scans/${item.id}`, { credentials: 'include' });
    if (!res.ok) { setError(await readError(res)); return; }
    const data = (await res.json()) as {
      suggestion?: Partial<LeadFormState> & {
        unassigned?: string[];
        meta?: { medianFontHeight?: number; nameFontHeight?: number; nameIndexPct?: number };
      };
    };
    const s = data.suggestion ?? {};
    openNew({
      name: s.name ?? '',
      company: s.company ?? '',
      jobTitle: s.jobTitle ?? '',
      email: s.email ?? '',
      phone: s.phone ?? '',
      website: s.website ?? '',
      street: s.street ?? '',
      zip: s.zip ?? '',
      city: s.city ?? '',
      country: s.country ?? '',
      source: 'scan',
    }, s.unassigned ?? []);
    setActiveScanId(item.id);

    const meta = s.meta;
    if (meta?.nameFontHeight !== undefined && meta.medianFontHeight) {
      setScanMeta({
        nameFontHeightRatio: meta.nameFontHeight / meta.medianFontHeight,
        ...(meta.nameIndexPct !== undefined ? { nameIndexPct: meta.nameIndexPct } : {}),
      });
    }
  }

  /** Discard a queued/failed scan without creating a lead. */
  async function discardScan(item: ScanItem) {
    const res = await fetch(`/api/leads/scans/${item.id}`, { method: 'DELETE', credentials: 'include' });
    if (!res.ok && res.status !== 204) { setError(await readError(res)); return; }
    void loadScans();
  }

  // ─── Delete ────────────────────────────────────────────────────────────────

  async function remove(lead: Lead) {
    const res = await fetch(`/api/leads/${lead.id}`, { method: 'DELETE', credentials: 'include' });
    if (!res.ok && res.status !== 204) { setError(await readError(res)); return; }
    void load();
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  const field = (
    label: string,
    key: keyof LeadFormState,
    opts: { type?: string; placeholder?: string } = {}
  ) => (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wide text-ink-muted mb-1">{label}</label>
      <input
        type={opts.type ?? 'text'}
        value={form[key] as string}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        placeholder={opts.placeholder}
        className="w-full rounded-lg bg-panel border border-line px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/30"
      />
    </div>
  );

  return (
    <Layout title={t('nav.leads', 'Leads')}>
      <div className="max-w-6xl space-y-5">
        <p className="text-sm text-ink-muted">
          {t('leads.intro', 'Erfasste Kontakte aus Visitenkarten-Scans, Kartentaps und manueller Eingabe. Exportiere sie als CSV oder einzeln als vCard.')}
        </p>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={scanning}
            className="inline-flex items-center gap-1.5 rounded-full bg-accent-600 px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
          >
            <Camera className="h-4 w-4" />
            {scanning ? t('leads.scanning', 'Erkenne Text…') : t('leads.scanCard', 'Visitenkarte scannen')}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic"
            multiple
            className="hidden"
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            onClick={() => openNew()}
            className="inline-flex items-center gap-1.5 rounded-full border border-line px-4 py-2 text-sm font-semibold text-ink-muted hover:bg-raised hover:text-ink"
          >
            <Plus className="h-4 w-4" />
            {t('leads.new', 'Neuer Lead')}
          </button>
          <a
            href="/api/leads/export.csv"
            className="inline-flex items-center gap-1.5 rounded-full border border-line px-4 py-2 text-sm font-semibold text-ink-muted hover:bg-raised hover:text-ink"
          >
            <Download className="h-4 w-4" />
            {t('leads.exportCsv', 'CSV-Export')}
          </a>

          <div className="flex w-full items-center gap-2 sm:ml-auto sm:w-auto">
            <div className="relative min-w-0 flex-1 sm:flex-none">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-faint" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('leads.search', 'Suchen…')}
                className="w-full sm:w-48 rounded-lg bg-panel border border-line pl-9 pr-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-accent-500/50"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="min-w-0 max-w-[45%] rounded-lg bg-panel border border-line px-3 py-2 text-sm text-ink focus:outline-none focus:border-accent-500/50 sm:max-w-none"
            >
              <option value="">{t('leads.allStatuses', 'Alle Status')}</option>
              {STATUS_KEYS.map((s) => (
                <option key={s} value={s}>{t(`leads.status.${s}`, s)}</option>
              ))}
            </select>
          </div>
        </div>

        {error && (
          <div role="alert" className="rounded-lg border border-danger-500/30 bg-danger-100 dark:bg-danger-500/10 px-4 py-3 text-sm text-danger-600 dark:text-danger-300">{error}</div>
        )}

        {/* Scan inbox — queued batch captures waiting for review */}
        {scans.length > 0 && (
          <div className="rounded-xl bg-panel border border-line p-4">
            <div className="mb-3 flex items-center gap-2">
              <Inbox className="h-4 w-4 text-ink-muted" />
              <h2 className="text-sm font-semibold text-ink">{t('leads.inbox', 'Scan-Eingang')}</h2>
              {hasOpenScans && (
                <span className="text-xs text-ink-faint">
                  {t('leads.inboxProcessing', 'Verarbeitung läuft im Hintergrund…')}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {scans.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-2 rounded-full border border-line bg-raised/50 pl-3 pr-1.5 py-1.5 text-sm"
                >
                  {(item.status === 'pending' || item.status === 'processing') && (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-accent-500" />
                      <span className="text-xs text-ink-muted">
                        {item.status === 'pending'
                          ? t('leads.inboxPending', 'Wartet…')
                          : t('leads.inboxScanning', 'Erkenne Text…')}
                      </span>
                    </>
                  )}
                  {item.status === 'done' && (
                    <>
                      <span className="text-xs font-medium text-success-600 dark:text-success-300">
                        {t('leads.inboxReady', 'Fertig')}
                      </span>
                      <button
                        type="button"
                        onClick={() => void reviewScan(item)}
                        className="rounded-full bg-accent-600 px-3 py-1 text-xs font-semibold text-white hover:opacity-90"
                      >
                        {t('leads.review', 'Prüfen')}
                      </button>
                    </>
                  )}
                  {item.status === 'failed' && (
                    <span className="text-xs text-danger-600 dark:text-danger-300">
                      {t('leads.inboxFailed', 'Erkennung fehlgeschlagen')}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => void discardScan(item)}
                    title={t('leads.discard', 'Verwerfen')}
                    aria-label={t('leads.discard', 'Verwerfen')}
                    className="rounded-full p-1 text-ink-faint hover:bg-raised hover:text-ink"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* List */}
        {leads === null && !error && <div className="h-40 rounded-xl bg-panel border border-line animate-pulse" />}
        {leads && leads.length === 0 && (
          <div className="rounded-xl bg-panel border border-line p-12 text-center text-ink-faint">
            {t('leads.empty', 'Noch keine Leads. Scanne eine Visitenkarte oder lege den ersten Kontakt manuell an.')}
          </div>
        )}

        {leads && leads.length > 0 && (
          <div className="rounded-xl bg-panel border border-line overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                  <th className="px-4 py-3">{t('leads.colName', 'Name')}</th>
                  <th className="px-4 py-3 hidden sm:table-cell">{t('leads.colCompany', 'Firma')}</th>
                  <th className="px-4 py-3 hidden md:table-cell">{t('leads.colContact', 'Kontakt')}</th>
                  <th className="px-4 py-3 hidden sm:table-cell">{t('leads.colStatus', 'Status')}</th>
                  <th className="px-4 py-3 hidden lg:table-cell">{t('leads.colSource', 'Quelle')}</th>
                  <th className="px-4 py-3 hidden lg:table-cell">{t('leads.colCreated', 'Erstellt')}</th>
                  <th className="px-4 py-3 text-right">{t('leads.colActions', 'Aktionen')}</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr key={lead.id} className="border-b border-line last:border-0 hover:bg-raised/50">
                    <td className="px-4 py-3">
                      <button type="button" onClick={() => openEdit(lead)} disabled={lead.access === 'view'} className="text-left disabled:cursor-default">
                        <p className="font-medium text-ink">{lead.name}</p>
                        {lead.jobTitle && <p className="text-xs text-ink-faint">{lead.jobTitle}</p>}
                      </button>
                      <div className="mt-1 space-y-0.5 text-xs text-ink-muted sm:hidden">
                        {lead.company && <p className="break-words">{lead.company}</p>}
                      </div>
                      <div className="mt-1 space-y-0.5 text-xs md:hidden">
                        {lead.email && <p className="break-all text-ink-muted">{lead.email}</p>}
                        {lead.phone && <p className="text-ink-faint">{lead.phone}</p>}
                      </div>
                      <span className={`mt-1.5 inline-block rounded-full border px-2.5 py-0.5 text-xs font-medium sm:hidden ${STATUS_STYLES[lead.status]}`}>
                        {t(`leads.status.${lead.status}`, lead.status)}
                      </span>
                      {(lead.access === 'view' || lead.access === 'edit') && (
                        <span className="mt-1 block w-fit rounded-full border border-line px-2 py-0.5 text-[11px] text-ink-muted">
                          {lead.access === 'edit' ? t('leads.share.sharedEdit', 'Mit dir geteilt (bearbeiten)') : t('leads.share.sharedView', 'Mit dir geteilt (nur lesen)')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-ink-muted hidden sm:table-cell">{lead.company ?? '—'}</td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      {lead.email && <p className="text-ink-muted">{lead.email}</p>}
                      {lead.phone && <p className="text-xs text-ink-faint">{lead.phone}</p>}
                      {!lead.email && !lead.phone && <span className="text-ink-faint">—</span>}
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <span className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_STYLES[lead.status]}`}>
                        {t(`leads.status.${lead.status}`, lead.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell text-ink-faint text-xs">
                      {t(`leads.source.${lead.source}`, lead.source)}
                      {lead.ownerEmail && <span className="block">{lead.ownerEmail}</span>}
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell text-ink-faint text-xs">
                      {new Date(lead.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-0.5 sm:gap-1">
                        {lead.access !== 'view' && (
                          <button
                            type="button"
                            onClick={() => openEdit(lead)}
                            title={t('leads.edit', 'Bearbeiten')}
                            className="rounded-lg p-2 text-ink-muted hover:bg-raised hover:text-ink"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                        )}
                        {(lead.access === 'owner' || lead.access === 'admin') && (
                          <button
                            type="button"
                            onClick={() => setSharing(lead)}
                            title={t('leads.share.title', 'Lead teilen')}
                            className="rounded-lg p-2 text-ink-muted hover:bg-raised hover:text-ink"
                          >
                            <Share2 className="h-4 w-4" />
                          </button>
                        )}
                        <a
                          href={`/api/leads/${lead.id}/vcf`}
                          title={t('leads.downloadVcf', 'Als vCard herunterladen')}
                          className="rounded-lg p-2 text-ink-muted hover:bg-raised hover:text-ink"
                        >
                          <FileDown className="h-4 w-4" />
                        </a>
                        {(lead.access === 'owner' || lead.access === 'admin') && (
                          <button
                            type="button"
                            onClick={() => setDeleting(lead)}
                            title={t('leads.delete', 'Löschen')}
                            className="rounded-lg p-2 text-ink-muted hover:bg-danger-500/10 hover:text-danger-500"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Editor modal */}
      {editing !== null && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 dark:bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setEditing(null)}
        >
          <form
            onSubmit={save}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg rounded-2xl bg-panel border border-line p-6 shadow-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="mb-5 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-ink">
                {editing === 'new'
                  ? (form.source === 'scan' ? t('leads.scannedTitle', 'Gescannte Visitenkarte') : t('leads.new', 'Neuer Lead'))
                  : t('leads.editLead', 'Lead bearbeiten')}
              </h3>
              <button type="button" onClick={() => setEditing(null)} className="rounded-lg p-1.5 text-ink-muted hover:bg-raised">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">{field(t('leads.fieldName', 'Name *'), 'name')}</div>
              {field(t('leads.fieldCompany', 'Firma'), 'company')}
              {field(t('leads.fieldJobTitle', 'Position'), 'jobTitle')}
              {field(t('leads.fieldEmail', 'E-Mail'), 'email', { type: 'email' })}
              {field(t('leads.fieldPhone', 'Telefon'), 'phone', { type: 'tel' })}
              <div className="sm:col-span-2">{field(t('leads.fieldWebsite', 'Website'), 'website')}</div>
              <div className="sm:col-span-2">{field(t('leads.fieldStreet', 'Straße & Nr.'), 'street')}</div>
              {field(t('leads.fieldZip', 'PLZ'), 'zip')}
              {field(t('leads.fieldCity', 'Stadt'), 'city')}
              <div className="sm:col-span-2">{field(t('leads.fieldCountry', 'Land'), 'country')}</div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold uppercase tracking-wide text-ink-muted mb-1">
                  {t('leads.fieldNote', 'Notiz')}
                </label>
                <textarea
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  rows={3}
                  className="w-full rounded-lg bg-panel border border-line px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/30"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-ink-muted mb-1">
                  {t('leads.colStatus', 'Status')}
                </label>
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value as Lead['status'] })}
                  className="w-full rounded-lg bg-panel border border-line px-3 py-2 text-sm text-ink focus:outline-none focus:border-accent-500/50"
                >
                  {STATUS_KEYS.map((s) => (
                    <option key={s} value={s}>{t(`leads.status.${s}`, s)}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Unassigned OCR lines — let the user map them to fields manually */}
            {unassigned.length > 0 && (
              <div className="mt-5 rounded-xl border border-warning-500/30 bg-warning-500/5 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-warning-600 dark:text-warning-300 mb-3">
                  {t('leads.unassignedTitle', 'Nicht automatisch zugeordnet — wähle, wo das hingehört:')}
                </p>
                <div className="space-y-2">
                  {unassigned.map((line) => (
                    <div key={line} className="flex items-center gap-2">
                      <p className="flex-1 min-w-0 truncate rounded-lg bg-panel border border-line px-3 py-1.5 text-sm text-ink" title={line}>
                        {line}
                      </p>
                      <select
                        value=""
                        onChange={(e) => {
                          const key = e.target.value as keyof LeadFormState | '';
                          if (key) assignLine(line, key);
                        }}
                        className="shrink-0 rounded-lg bg-panel border border-line px-2 py-1.5 text-xs text-ink focus:outline-none focus:border-accent-500/50"
                      >
                        <option value="" disabled>{t('leads.assignTo', 'Zuordnen…')}</option>
                        {ASSIGNABLE_FIELDS.map((f) => (
                          <option key={f.key} value={f.key}>{t(f.labelKey, f.fallback)}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setUnassigned((prev) => prev.filter((l) => l !== line))}
                        title={t('leads.ignoreLine', 'Ignorieren')}
                        aria-label={t('leads.ignoreLine', 'Ignorieren')}
                        className="shrink-0 rounded-lg p-1.5 text-ink-faint hover:bg-raised hover:text-ink"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="rounded-full border border-line px-5 py-2 text-sm font-semibold text-ink-muted hover:bg-raised"
              >
                {t('common.cancel', 'Abbrechen')}
              </button>
              <button
                type="submit"
                disabled={saving}
                className="rounded-full bg-accent-600 px-5 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
              >
                {t('common.save', 'Speichern')}
              </button>
            </div>
          </form>
        </div>
      )}

      {sharing && <LeadShareDialog leadId={sharing.id} leadName={sharing.name} onClose={() => setSharing(null)} />}

      {/* Delete confirmation */}
      <Modal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        type="confirm"
        title={t('leads.deleteTitle', 'Lead löschen?')}
        message={deleting ? t('leads.deleteMessage', '„{{name}}“ wird endgültig gelöscht.', { name: deleting.name }) : ''}
        confirmText={t('leads.delete', 'Löschen')}
        cancelText={t('common.cancel', 'Abbrechen')}
        onConfirm={() => { if (deleting) void remove(deleting); }}
      />
    </Layout>
  );
}


/** Leads is a Business feature: other plans see the upgrade prompt instead of the page. */
export default function Leads() {
  const { t } = useAppTranslation();
  const { hasFeature, loading } = useTier();
  if (loading) return <Layout title={t('nav.leads', 'Leads')}><div /></Layout>;
  if (!hasFeature('leads')) {
    return (
      <Layout title={t('nav.leads', 'Leads')}>
        <div className="max-w-xl p-4 sm:p-6 lg:p-8">
          <UpgradePrompt requiredTier="business" message={t('paywall.leads', 'Leads und der Scan von Visitenkarten sind im Plan Business enthalten.')} />
        </div>
      </Layout>
    );
  }
  return <LeadsPage />;
}
